import { ItemEditor as CampfireItemEditor } from "@campfirelearning/sdk-lti";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { getAlignmentsSafely, gql } from "@examspark/api";
import {
  parseConnectionArray,
  parseObjectiveScope,
  reactKeyFromIDSK,
} from "@examspark/common";
import {
  Button,
  ButtonMenu,
  CautionBanner,
  ConfirmModal,
  ContainerButton,
  DragHandle,
  LargePlaceholder,
  Menu,
  MenuButtons,
  MenuItem,
  ObjectiveBadge,
  Sortable,
  Toolbar,
  Tooltip,
  Truncate,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconDownArrowLine,
  IconLayersLine,
  IconMagicLine,
  IconMoreLine,
  IconPencilLine,
  IconSearchLine,
  IconSwapLine,
  IconTrashLine,
  IconUpArrowLine,
  IconUserCircledLine,
  IconDocumentLine,
} from "@examspark/common-ui/dist/components/icons";
import { colors } from "@examspark/common-ui/dist/themes";
import { Resource } from "@examspark/types";

import { useAuthorizationContext } from "../../../hooks/useAuthorizationContext";
import { useModal } from "../../../hooks/useModal";
import { useAppContext } from "../../../providers/AppContext";
import { ExamEditorContext } from "../../exam-editor/examEditor";
import ItemEditorIframedModal from "../../items/ItemEditorIframedModal";
import ItemPreview from "../../items/ItemPreview";

import { ActivityEditorConfigContext } from "./ActivityEditor";
import ArrangeItemsModal from "./ArrangeItemsModal";
import CopyItemBeforeEditConfirmModal from "./CopyItemConfirmModal";
import ExamBuilderModal from "./exam-builder/ExamBuilderModal";
import { useElementSize } from "./hooks/useElementSize";
import ItemPickerModal from "./item-picker/ItemPickerModal";
import PassagePickerModal from "./passage-picker/PassagePickerModal";

const EditorLayout = styled.div`
  display: flex;
  min-height: 0;
  height: 100%;
`;
const EditorLayoutItems = styled.div`
  width: 35rem;
  border-right: solid 3px #ddd;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const EditorLayoutItemsList = styled.div`
  overflow: auto;
  flex: 1;
`;
const StyledItemListHeaderToolbar = styled(Toolbar)`
  border-bottom: solid 1px #ddd;
  padding: 0.7rem;
  background-color: #f7f7f7;
`;
const StyledItemListFooterToolbar = styled(Toolbar)`
  flex-direction: column;
  border-top: solid 1px #ddd;
  padding: 0.7rem;
`;

const EditorLayoutPreview = styled.div`
  flex: 1;
  overflow: auto;
`;
const EditorLayoutItemsOffset = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.2rem;
`;
export const SortableMeta = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;
const SortableButtons = styled.div`
  display: flex;
  /* flex-direction: column; */
  gap: 0.2rem;

  button svg {
    fill: #999;
  }
  button:hover svg {
    fill: #555;
  }
`;

type NewProps = {
  selected?: boolean;
};

export const SortableItemCard = styled(ContainerButton)<NewProps>`
  display: flex;
  align-items: center;
  flex-direction: row;
  background-color: #f7f7f7;
  padding: 1rem 0.5rem;
  width: 100%;
  gap: 1rem;

  ${(props) =>
    props.selected &&
    `
  border-left: solid 3px #006699 !important;
  padding-left: 0.3rem;
  `}
`;

const Badges = styled.div`
  display: flex;
  /* flex-wrap: wrap; */
  gap: 0.3rem;
  overflow: hidden;

  :not(:first-child) {
    margin-top: 0.5rem;
  }
`;

export const ActivityEditorItems = (props: {
  onCancel?: () => void;
  onNext?: () => void;
  isCreating?: boolean;
  onRequestCreate?: () => void;
  itemPickerModal?: typeof ItemPickerModal;
  browseButtonTitle?: string;
  browseButtonColor?: colors;
  createItemButtonTitle?: string;
  createItemButtonColor?: colors;
}) => {
  // const [appState] = useAppContext();
  // const preferredAuthorities = getObjectiveAuthorityIdsFromAppState(appState);
  // const win = useContext(WindowContext);
  const {
    browseButtonTitle = "Items",
    browseButtonColor = undefined,
    createItemButtonTitle = "Item",
    createItemButtonColor = "create",
  } = props;

  const consumer = useRef<CampfireItemEditor | null>(null);
  const win = { device: "desktop" };
  const [state, dispatch] = useContext(ExamEditorContext);
  const [previewItem, setPreviewItem] = useState<Resource>();
  const [appContext] = useAppContext();
  const config = useContext(ActivityEditorConfigContext);

  const [appState] = useAppContext();
  const preferredAuthorities = appState.userPrefs?.objectiveAuthorityIds;

  const itemEditorModal = useModal();
  const itemPickerModal = useModal();
  const passagePickerModal = useModal();
  const guidedBuilderModal = useModal();
  const arrangeItemsModal = useModal();

  const [itemToSwapAtIndex, setItemToSwapAtIndex] = useState<number>();
  const [itemIdToBeCloned, setItemIdToBeCloned] = useState<string>();
  const [itemIdToEdit, setItemIdToEdit] = useState<string>();

  const ref = useRef<HTMLDivElement>(null);
  const size = useElementSize(ref);

  const hasContexts = useMemo(() => {
    return state.questions.some((q) => q.contexts?.length);
  }, [state.questions]);

  // todo: Pass in better appcontext from top level?
  // const userContentBankId = context.appLicenseConfig?.userContentBankId;
  const userContentBankId = appContext.appLicenseConfig?.userContentBankId as
    | string
    | undefined;

  const authorizationContext = useAuthorizationContext();

  useEffect(() => {
    if (state.questions.length > 0) {
      // console.log("setting preview item", state.questions[0]);
      setPreviewItem(state.questions[0]);
    }
  }, [state.questions]);

  useEffect(() => {
    const abortController = new AbortController();
    // console.log("adding listener");

    window.addEventListener(
      "message",
      (event) => {
        if (
          event.data.type === "message" &&
          event.data.name === "setItemEditorData"
        ) {
          // console.log("event", event);
          // console.log("set item editor data (SDKExamBuilder)", consumer, event);
          consumer.current?.setItemContent(event.data.data);
        }
      },
      {
        signal: abortController.signal,
      },
    );

    return () => {
      return abortController.abort();
    };
  }, []);

  const handlePreviousItem = () => {
    const index = state.questions.findIndex((q) => q.id === previewItem?.id);
    // console.log("prev", index, previewItem?.id);
    if (index === 0) return;
    setPreviewItem(state.questions[index - 1]);
  };

  const handleNextItem = () => {
    const index = state.questions.findIndex((q) => q.id === previewItem?.id);
    // console.log("next", index, previewItem?.id);
    if (index < state.questions.length - 1) {
      setPreviewItem(state.questions[index + 1]);
    }
  };

  const handleSwapAtIndex = async (idx: number) => {
    setItemToSwapAtIndex(idx);
    itemPickerModal.open();
  };

  const handleCustomizeItem = async (item: Resource) => {
    // console.log("customizing", item);
    if (item.workspaceId === undefined) {
      console.error(
        "Item workspaceId is undefined. Cannot decide whether to edit or customize.",
      );
      return;
    }

    // if the item is created by the current user, open the editor
    // for the current item, otherwise, let's clone the item and edit that item.
    // console.log("edit exiting item", item.creatorId, appContext.user?.id);
    if (
      item.creatorId != null &&
      item.creatorId === appContext.user?.id &&
      item.workspaceId === appContext.workspace?.id
    ) {
      setItemIdToEdit(item.id);
      itemEditorModal.open();
      return;
    } else {
      if (userContentBankId == null) {
        console.error(
          "Cannot clone items because current workspace does not have a user content bank configured.",
        );
        alert(
          "There is a configuration problem with your workspace. Please contact support.",
        );
      }
      setItemIdToBeCloned(item.id);
    }
  };

  const findDraggable = (active: { id: string | number }) => {
    return state.questions.find((q) => q.id === active.id);
  };
  const findDragIndex = (active: { id: string | number }) => {
    return state.questions.findIndex((q) => q.id === active.id) + 1;
  };

  const itemPickerProps: Partial<React.ComponentProps<typeof ItemPickerModal>> =
    {
      selected: state.questions,
      onRequestClose: () => {
        itemPickerModal.close();
        setItemToSwapAtIndex(undefined);
      },
      initialFilter:
        itemToSwapAtIndex !== undefined
          ? {
              subject:
                state.questions[itemToSwapAtIndex].subject?.[0] != null
                  ? [state.questions[itemToSwapAtIndex].subject?.[0] ?? ""]
                  : undefined,
              language:
                state.questions[itemToSwapAtIndex].language?.[0] != null
                  ? [state.questions[itemToSwapAtIndex].language?.[0] ?? ""]
                  : undefined,
              alignmentIds: getAlignmentsSafely(
                state.questions[itemToSwapAtIndex].alignments,
              )
                ?.filter((a) => {
                  const objectiveAuthorityIds =
                    appContext.userPrefs?.objectiveAuthorityIds;
                  if (objectiveAuthorityIds == null) return true;
                  return objectiveAuthorityIds?.includes(a?.authorityId ?? "");
                })
                ?.map((a) => a.id),
            }
          : undefined,
      onSubmit: (items) => {
        if (itemToSwapAtIndex !== undefined) {
          dispatch({
            type: "REPLACE_ITEM_AT_INDEX",
            index: itemToSwapAtIndex,
            questions: items,
          });
        } else {
          dispatch({
            type: "ADD_ITEMS",
            questions: items,
          });
        }
        itemPickerModal.close();
      },
    };

  return (
    <>
      <ArrangeItemsModal {...arrangeItemsModal.modalProps} />
      {userContentBankId != null && (
        <CopyItemBeforeEditConfirmModal
          itemId={itemIdToBeCloned}
          isOpen={!!itemIdToBeCloned}
          onRequestClose={() => {
            setItemIdToBeCloned(undefined);
          }}
          onClone={async (newItem) => {
            setItemIdToBeCloned(undefined);
            console.log("new item", newItem);
            dispatch({
              type: "REPLACE_ITEM_AT_INDEX",
              index: state.questions.findIndex(
                (q) => q.id === itemIdToBeCloned,
              ),
              questions: [newItem],
            });

            if (newItem.id) {
              setItemIdToEdit(newItem.id);
              itemEditorModal.open();
            }
          }}
          targetBankId={userContentBankId}
        />
      )}
      <ItemEditorIframedModal
        {...itemEditorModal.modalProps}
        onRequestClose={() => {
          itemEditorModal.modalProps.onRequestClose?.();
          setItemIdToEdit(undefined);
        }}
        onInit={(editor) => {
          consumer.current = editor;
        }}
        createWithTags={config.createWithTags}
        objectiveAuthorityIds={preferredAuthorities}
        itemId={itemIdToEdit}
        onSave={async (item: { id: string }) => {
          const newItemId = item.id;
          if (!newItemId) {
            console.error("Failed to create item");
            return;
          }

          const newItemData = await gql.operations.item.getItemForExam(
            newItemId,
            authorizationContext,
          );

          if (!newItemData) {
            console.error("Failed to get item data for exam", newItemId);
            return;
          }

          if (itemIdToEdit) {
            // console.log("editing existing");
            dispatch({
              type: "REPLACE_ITEM_AT_INDEX",
              index: state.questions.findIndex((q) => q.id === itemIdToEdit),
              questions: [newItemData],
            });
          } else {
            // console.log("adding item", newItemData);
            dispatch({
              type: "ADD_ITEMS",
              questions: [newItemData],
            });
          }

          setItemIdToEdit(undefined);
          itemEditorModal.close();
        }}
      />
      <PassagePickerModal
        {...passagePickerModal.modalProps}
        onSubmit={(items) => {
          dispatch({
            type: "ADD_ITEMS",
            questions: items,
          });
          passagePickerModal.close();
        }}
      />
      {props.itemPickerModal ? (
        props.itemPickerModal({
          ...itemPickerModal.modalProps,
          ...itemPickerProps,
        })
      ) : (
        <ItemPickerModal
          {...itemPickerModal.modalProps}
          {...itemPickerProps}
          title="Select Items"
        />
      )}
      <ExamBuilderModal
        {...guidedBuilderModal.modalProps}
        onBuild={(items) => {
          dispatch({
            type: "ADD_ITEMS",
            questions: items,
          });
          guidedBuilderModal.close();
        }}
      />
      <EditorLayout data-cy="activity-editor-tab-items">
        {win.device !== "phone" && (
          <EditorLayoutItems>
            {!state.questions.length && (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <LargePlaceholder>No Items Yet</LargePlaceholder>
              </div>
            )}
            {!!state.questions.length && (
              <>
                <StyledItemListHeaderToolbar>
                  <Button
                    title="Previous"
                    variant="plain"
                    icon={<IconUpArrowLine />}
                    disabled={
                      state.questions.findIndex(
                        (q) => q.id === previewItem?.id,
                      ) < 1
                    }
                    onClick={handlePreviousItem}
                  />
                  <Button
                    title="Next"
                    variant="plain"
                    icon={<IconDownArrowLine />}
                    disabled={
                      state.questions.findIndex(
                        (q) => q.id === previewItem?.id,
                      ) >
                      state.questions.length - 2
                    }
                    onClick={handleNextItem}
                  />
                  <Toolbar.Flex />
                  <Button
                    title="Arrange Items"
                    variant="plain"
                    disabled={hasContexts || config.readOnly}
                    onClick={() => {
                      arrangeItemsModal.open();
                    }}
                  >
                    <IconLayersLine height="16" />
                  </Button>
                  <Button
                    title="Remove All"
                    color="danger"
                    variant="plain"
                    disabled={config.readOnly}
                    onClick={async () => {
                      const result = await ConfirmModal.show({
                        children: (
                          <CautionBanner variant="plain">
                            Are you sure you want to remove all items from this
                            activity?
                          </CautionBanner>
                        ),
                      });
                      if (!result) return;
                      dispatch({
                        type: "UPDATE",
                        data: {
                          ...state,
                          questions: [],
                        },
                      });
                    }}
                  >
                    <IconTrashLine width="16" />
                  </Button>
                </StyledItemListHeaderToolbar>
                <EditorLayoutItemsList>
                  <EditorLayoutItemsOffset>
                    <Sortable
                      lockAxis="y"
                      useDragHandle
                      disabled={config.readOnly}
                      lockToContainerEdges
                      accessibility={{
                        announcements: {
                          onDragStart({ active }) {
                            return `Picked up item ${
                              findDraggable(active)?.name
                            }}. Item is in position ${findDragIndex(
                              active,
                            )} of ${state.questions.length}`;
                          },
                          onDragOver({ active, over }) {
                            if (over) {
                              return `Item ${
                                findDraggable(active)?.name
                              } is in position ${findDragIndex(over)} of ${
                                state.questions.length
                              }`;
                            }
                          },
                          onDragEnd({ active, over }) {
                            if (over) {
                              return `Item ${
                                findDraggable(active)?.name
                              } is now in position ${findDragIndex(over)} of ${
                                state.questions.length
                              }`;
                            }
                          },
                          onDragCancel() {
                            return `Dragging ended`;
                          },
                        },
                        screenReaderInstructions: {
                          draggable: `
                            To sort the items in your activity, press space or enter.
                            Use the up and down arrow keys to update the position of
                            the item in the exam.
                            Press space or enter again to finish sorting that item.
                          `,
                        },
                      }}
                      onSortEnd={({ oldIndex, newIndex }) => {
                        dispatch({
                          type: "REORDER_ITEMS",
                          oldIndex,
                          newIndex,
                        });
                      }}
                    >
                      {state.questions.map((q: Resource, idx: number) => (
                        <ListItem
                          idx={idx}
                          key={reactKeyFromIDSK(q)}
                          item={q}
                          isSelected={q.id === previewItem?.id}
                          onSelect={(item) => setPreviewItem(item)}
                          onEdit={(item) => handleCustomizeItem(item)}
                          onSwapAtIndex={(idx) => handleSwapAtIndex(idx)}
                        />
                      ))}
                    </Sortable>
                  </EditorLayoutItemsOffset>
                </EditorLayoutItemsList>
              </>
            )}
            <StyledItemListFooterToolbar>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  flex: 1,
                  width: "100%",
                }}
              >
                {config.guidedBuilder?.show && (
                  <Button
                    title="Launch Guided Builder"
                    icon={<IconMagicLine />}
                    disabled={config.readOnly}
                    onClick={() => guidedBuilderModal.open()}
                    stretch
                  >
                    Builder
                  </Button>
                )}
                {/* <Button
                  title="Debug State"
                  stretch
                  onClick={() => console.log(state)}
                >
                  Debug
                </Button> */}
                <Button
                  title={browseButtonTitle}
                  color={browseButtonColor}
                  icon={<IconSearchLine />}
                  stretch
                  disabled={config.readOnly}
                  onClick={() => itemPickerModal.open()}
                >
                  {browseButtonTitle}
                </Button>
                {config.enablePassagePicker && (
                  <Button
                    title="Select Passage"
                    icon={<IconSearchLine />}
                    stretch
                    disabled={config.readOnly}
                    onClick={() => passagePickerModal.open()}
                  >
                    Passages
                  </Button>
                )}
              </div>
              {config?.allowItemCreation && (
                <Button
                  title={createItemButtonTitle}
                  color={createItemButtonColor}
                  icon={<IconAddLine />}
                  disabled={config.readOnly}
                  onClick={() => itemEditorModal.open()}
                  stretch
                >
                  {createItemButtonTitle}
                </Button>
              )}
            </StyledItemListFooterToolbar>
          </EditorLayoutItems>
        )}
        <EditorLayoutPreview ref={ref}>
          {!previewItem?.id && (
            <div
              style={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LargePlaceholder>Nothing to Preview Yet</LargePlaceholder>
            </div>
          )}
          {previewItem?.id && (
            <ItemPreview
              itemId={previewItem?.id}
              condensed={(size?.width ?? 0) < 600}
              showAutoRevealKey
            />
          )}
        </EditorLayoutPreview>
      </EditorLayout>
    </>
  );
};

export const ListItem = (props: {
  idx: number;
  item: Resource;
  isSelected?: boolean;
  onSelect?: (item: Resource) => void;
  onEdit?: (item: Resource) => void;
  onSwapAtIndex?: (index: number) => void;
}) => {
  const menu = useModal();
  const [appState] = useAppContext();
  const preferredAuthorities = appState.userPrefs?.objectiveAuthorityIds;
  const [, dispatch] = useContext(ExamEditorContext);

  const config = useContext(ActivityEditorConfigContext);
  const userContentBankId = `${appState.workspace?.id}/user-content`;

  const q = props.item;
  const idx = props.idx;
  const contexts = parseConnectionArray(q.contexts);
  return (
    <SortableItemCard
      key={q.id}
      onClick={() => props.onSelect?.(q)}
      selected={props.isSelected}
    >
      <Sortable.Handle>
        <DragHandle direction="vertical" />
      </Sortable.Handle>
      <SortableMeta>
        <Truncate>{q.name}</Truncate>
        <div>
          <Badges>
            {q.creatorId === appState.user?.id && (
              <div
                style={{
                  height: "2.2rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconUserCircledLine width="14" />
              </div>
            )}

            {getAlignmentsSafely(q.alignments)?.map((obj) => {
              const scope = parseObjectiveScope(obj.scope ?? "");
              if (
                scope &&
                preferredAuthorities?.length &&
                !preferredAuthorities.includes(scope.authority ?? "")
              ) {
                return null;
              }

              return (
                <ObjectiveBadge key={obj.id}>{obj.abbreviation}</ObjectiveBadge>
              );
            })}
            {!!contexts.length && (
              <Tooltip
                tip={
                  <>
                    {contexts.map((c) => (
                      <div key={c.id}>{c.name}</div>
                    ))}
                  </>
                }
              >
                <div
                  style={{
                    height: "2.2rem",
                    display: "flex",
                    alignItems: "center",
                    color: "#666",
                    gap: "0.3rem",
                  }}
                >
                  <div>
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                      }}
                    >
                      <IconDocumentLine width="14" fill="#666" />
                    </div>
                  </div>
                  <div
                    style={{
                      maxWidth: "20rem",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {contexts.map((c) => c.name).join(", ")}
                  </div>
                </div>
              </Tooltip>
            )}
          </Badges>
        </div>
      </SortableMeta>
      <SortableButtons>
        <ButtonMenu
          placement="bottom-end"
          button={
            <Button
              variant="plain"
              icon={<IconMoreLine />}
              title="more options"
              disabled={config.readOnly}
              onClick={(e) => {
                e.stopPropagation();
                menu.open();
              }}
            />
          }
          expanded={menu.isOpen}
          onExpandedChange={menu.setIsOpen}
        >
          <Menu>
            <MenuButtons>
              <MenuItem
                title="Customize"
                icon={<IconSwapLine />}
                onClick={() => {
                  props.onSwapAtIndex?.(idx);
                  menu.close();
                }}
              >
                Swap Item
              </MenuItem>
              {config?.allowItemCreation &&
                config?.allowItemCustomization &&
                userContentBankId && (
                  <MenuItem
                    title="Edit Item"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onEdit?.(q);
                      menu.close();
                    }}
                    icon={<IconPencilLine width="16px" />}
                  >
                    Customize Item
                  </MenuItem>
                )}
              <MenuItem
                title="Delete"
                onClick={(e) => {
                  // don't also set the preview item
                  e.stopPropagation();

                  dispatch({
                    type: "REMOVE_ITEM_AT_INDEX",
                    index: idx,
                  });

                  menu.close();
                }}
                icon={<IconTrashLine />}
              >
                Delete Item
              </MenuItem>
            </MenuButtons>
          </Menu>
        </ButtonMenu>
      </SortableButtons>
    </SortableItemCard>
  );
};

export default ActivityEditorItems;
